import React from 'react';
import { Link } from 'react-router';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { PlaceholderImage } from '../../../components/PlaceholderImage';
import { Section } from '../../../components/Section';
import { MetaRow } from '../../../components/MetaRow';
import { CiCalendar, CiUser, CiMap, CiClock2 } from 'react-icons/ci';
import useTheme from '../../../hooks/useTheme';

export const Hero: React.FC = () => {
    const { theme } = useTheme();
    const scrollToFeatures = () => {
        const featuresSection = document.getElementById('features');
        if (featuresSection) {
            featuresSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <Section
            id="hero"
            style={{
                background: `linear-gradient(180deg, ${theme.background.panel} 0%, ${theme.background.default} 100%)`,
                position: 'relative',
                overflow: 'hidden',
                paddingTop: '100px',
                paddingBottom: '100px',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '-100px',
                    right: '-100px',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${theme.colors.accent.korea} 0%, transparent 70%)`,
                    opacity: 0.15,
                    filter: 'blur(60px)',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    bottom: '-100px',
                    left: '-100px',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${theme.colors.accent.egypt} 0%, transparent 70%)`,
                    opacity: 0.15,
                    filter: 'blur(60px)',
                }}
            />

            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '48px',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                }}
                className="heroGrid"
            >
                <div>
                    <div
                        style={{
                            display: 'flex',
                            gap: '12px',
                            marginBottom: '24px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Badge status="LIVE">Phase 1: Launch Site</Badge>
                        <Badge status="COMING SOON">Phase 2: Portal</Badge>
                    </div>

                    <h1
                        style={{
                            fontSize: '46px',
                            fontWeight: 700,
                            color: theme.text.primary,
                            lineHeight: 1.35,
                            marginBottom: '20px',
                            letterSpacing: '-0.5px',
                        }}
                        className="heroTitle"
                    >
                        BUSAN–CAIRO BUSINESS HUB (BCBH)
                    </h1>

                    <p
                        style={{
                            fontSize: '18px',
                            color: theme.text.muted,
                            lineHeight: 1.6,
                            marginBottom: '32px',
                        }}
                    >
                        A Korea–Egypt bridge for knowledge exchange, partnerships, and project opportunities — launched by BTEC, LOHMANN ECCE, and BTP.
                    </p>

                    <ul
                        style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: '0 0 32px 0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}
                    >
                        {[
                            'Matchmaking between Korean & Egyptian companies',
                            'Training & technology know-how transfer',
                            'Tender collaboration and JV support (coming soon)',
                        ].map((item, idx) => (
                            <li
                                key={idx}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    color: theme.text.primary,
                                    fontSize: '16px',
                                }}
                            >
                                <span
                                    style={{
                                        width: '6px',
                                        height: '6px',
                                        borderRadius: '50%',
                                        backgroundColor: theme.colors.green.g1,
                                        flexShrink: 0,
                                    }}
                                />
                                {item}
                            </li>
                        ))}
                    </ul>

                    <div
                        style={{
                            display: 'flex',
                            gap: '16px',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button variant="primary" onClick={scrollToFeatures}>
                            Explore the Hub
                        </Button>
                        <Link to="/contact" style={{ textDecoration: 'none' }}>
                            <Button variant="secondary">Contact the Team</Button>
                        </Link>
                    </div>
                </div>

                <div>
                    <PlaceholderImage
                        label="BCBH Hub: Busan ↔ Cairo"
                        height={400}
                        style={{ marginBottom: '24px' }}
                    />
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: '12px',
                        }}
                        className="heroStats"
                    >
                        {[
                            { label: 'Partners', value: '3 founding entities' },
                            { label: 'Offices', value: 'Cairo + Busan' },
                            { label: 'Meetings', value: 'virtual & in-person' },
                        ].map((stat, idx) => (
                            <Card key={idx} style={{ padding: '16px', textAlign: 'center' }}>
                                <div
                                    style={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: theme.colors.green.g1,
                                        marginBottom: '4px',
                                    }}
                                >
                                    {stat.label}
                                </div>
                                <div
                                    style={{
                                        fontSize: '13px',
                                        color: theme.text.muted,
                                    }}
                                >
                                    {stat.value}
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </div>

            {/* MoU Facts Strip */}
            <div
                style={{
                    marginTop: '64px',
                    paddingTop: '32px',
                    borderTop: `1px solid ${theme.colors.line}`,
                    position: 'relative',
                    zIndex: 1,
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '24px',
                    }}
                    className="mouFactsGrid"
                >
                    <div>
                        <MetaRow
                            icon={<CiCalendar />}
                            label="Effective Date"
                            value="26 Mar 2025"
                        />
                    </div>
                    <div>
                        <MetaRow
                            icon={<CiUser />}
                            label="Founding Parties"
                            value="BTEC • LOHMANN ECCE • BTP"
                        />
                    </div>
                    <div>
                        <MetaRow
                            icon={<CiMap />}
                            label="Presence"
                            value="Cairo + Busan"
                        />
                    </div>
                    <div>
                        <MetaRow
                            icon={<CiClock2 />}
                            label="Meetings"
                            value="Virtual or physical"
                        />
                    </div>
                </div>
            </div>
        </Section>
    );
};

